import React, { useState, useEffect } from 'react';
import { rhuService } from '../../../../services/rhuService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as MedicalIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon
} from '@mui/icons-material';

const Services = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedSubcategories, setExpandedSubcategories] = useState([]);
  
  // Edit mode states
  const [editMode, setEditMode] = useState(null); // 'category', 'subcategory', 'service', or null
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');

  // Load from Supabase on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const result = await rhuService.getAllServicesHierarchy();
    if (result.success) {
      setCategories(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const openAddCategoryModal = () => {
    setEditMode(null);
    setEditingItem(null);
    setCategoryName('');
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setEditMode('category');
    setEditingItem(category);
    setCategoryName(category.name);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;

    setLoading(true);
    let result;
    
    if (editMode === 'category' && editingItem) {
      result = await rhuService.updateCategory(editingItem.id, { name: categoryName });
      if (result.success) {
        setSuccess('Category updated successfully');
      }
    } else {
      result = await rhuService.addCategory({ name: categoryName });
      if (result.success) {
        setSuccess('Category added successfully');
      }
    }
    
    if (result.success) {
      setCategoryName('');
      setShowCategoryModal(false);
      setEditMode(null);
      setEditingItem(null);
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const openAddSubcategoryModal = (category) => {
    setEditMode(null);
    setEditingItem(null);
    setSelectedCategory(category);
    setSubcategoryName('');
    setShowSubcategoryModal(true);
  };

  const openEditSubcategoryModal = (category, subcategory) => {
    setEditMode('subcategory');
    setEditingItem(subcategory);
    setSelectedCategory(category);
    setSubcategoryName(subcategory.name);
    setShowSubcategoryModal(true);
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryName.trim() || !selectedCategory) return;

    setLoading(true);
    let result;
    
    if (editMode === 'subcategory' && editingItem) {
      result = await rhuService.updateSubcategory(editingItem.id, { name: subcategoryName });
      if (result.success) {
        setSuccess('Subcategory updated successfully');
      }
    } else {
      result = await rhuService.addSubcategory({
        category_id: selectedCategory.id,
        name: subcategoryName
      });
      if (result.success) {
        setSuccess('Subcategory added successfully');
      }
    }
    
    if (result.success) {
      setSubcategoryName('');
      setShowSubcategoryModal(false);
      setSelectedCategory(null);
      setEditMode(null);
      setEditingItem(null);
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const openAddServiceModal = (category, subcategory) => {
    setEditMode(null);
    setEditingItem(null);
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    setServiceName('');
    setServicePrice('');
    setServiceDescription('');
    setShowServiceModal(true);
  };

  const openEditServiceModal = (category, subcategory, service) => {
    setEditMode('service');
    setEditingItem(service);
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    setServiceName(service.name);
    setServicePrice(service.price.toString());
    setServiceDescription(service.description || '');
    setShowServiceModal(true);
  };

  const handleSaveService = async () => {
    if (!serviceName.trim() || !servicePrice || !selectedCategory || !selectedSubcategory) return;

    setLoading(true);
    let result;
    
    if (editMode === 'service' && editingItem) {
      result = await rhuService.updateService(editingItem.id, {
        name: serviceName,
        price: parseFloat(servicePrice),
        description: serviceDescription
      });
      if (result.success) {
        setSuccess('Service updated successfully');
      }
    } else {
      result = await rhuService.addService({
        subcategory_id: selectedSubcategory.id,
        name: serviceName,
        price: parseFloat(servicePrice),
        description: serviceDescription
      });
      if (result.success) {
        setSuccess('Service added successfully');
      }
    }
    
    if (result.success) {
      setServiceName('');
      setServicePrice('');
      setServiceDescription('');
      setShowServiceModal(false);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setEditMode(null);
      setEditingItem(null);
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const toggleCategory = (categoryId) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  const toggleSubcategory = (subcategoryId) => {
    if (expandedSubcategories.includes(subcategoryId)) {
      setExpandedSubcategories(expandedSubcategories.filter(id => id !== subcategoryId));
    } else {
      setExpandedSubcategories([...expandedSubcategories, subcategoryId]);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category and all its contents?')) return;

    setLoading(true);
    const result = await rhuService.deleteCategory(categoryId);
    
    if (result.success) {
      setSuccess('Category deleted successfully');
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const deleteSubcategory = async (categoryId, subcategoryId) => {
    if (!window.confirm('Are you sure you want to delete this subcategory and all its services?')) return;

    setLoading(true);
    const result = await rhuService.deleteSubcategory(subcategoryId);
    
    if (result.success) {
      setSuccess('Subcategory deleted successfully');
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const deleteService = async (categoryId, subcategoryId, serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    setLoading(true);
    const result = await rhuService.deleteService(serviceId);
    
    if (result.success) {
      setSuccess('Service deleted successfully');
      loadAllData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicalIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              RHU Services Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage service categories, subcategories, and pricing
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={openAddCategoryModal}
            size="large"
          >
            Add Category
          </Button>
        </Box>

        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Categories List */}
        {categories.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', p: 8, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
            <FolderOpenIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No Categories Yet</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Start by adding your first service category to organize your RHU services
            </Typography>
            <Button 
              variant="contained" 
              onClick={openAddCategoryModal}
              startIcon={<AddIcon />}
            >
              Create First Category
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {categories.map((category) => (
              <Card 
                key={category.id} 
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.light', boxShadow: 2 }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Category Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, cursor: 'pointer' }} onClick={() => toggleCategory(category.id)}>
                      <IconButton size="small" sx={{ bgcolor: expandedCategories.includes(category.id) ? 'primary.light' : 'action.hover', color: expandedCategories.includes(category.id) ? 'primary.contrastText' : 'inherit' }}>
                        {expandedCategories.includes(category.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <FolderIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {category.name}
                      </Typography>
                      <Chip label={`${category.subcategories.length} subcategories`} size="small" color="primary" variant="outlined" />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Edit Category">
                        <IconButton size="small" color="info" onClick={() => openEditCategoryModal(category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add Subcategory">
                        <IconButton size="small" color="success" onClick={() => openAddSubcategoryModal(category)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Category">
                        <IconButton size="small" color="error" onClick={() => deleteCategory(category.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Subcategories */}
                  <Collapse in={expandedCategories.includes(category.id)}>
                    <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {category.subcategories.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                          No subcategories yet. Add one to get started!
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {category.subcategories.map((subcategory) => (
                            <Paper 
                              key={subcategory.id} 
                              elevation={0} 
                              variant="outlined" 
                              sx={{ p: 2, bgcolor: 'grey.50' }}
                            >
                              {/* Subcategory Header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, cursor: 'pointer' }} onClick={() => toggleSubcategory(subcategory.id)}>
                                  <IconButton size="small">
                                    {expandedSubcategories.includes(subcategory.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                  <CategoryIcon color="success" fontSize="small" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {subcategory.name}
                                  </Typography>
                                  <Chip label={`${subcategory.services.length} services`} size="small" size="small" />
                                </Box>
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title="Edit Subcategory">
                                    <IconButton size="small" color="info" onClick={() => openEditSubcategoryModal(category, subcategory)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Add Service">
                                    <IconButton size="small" color="success" onClick={() => openAddServiceModal(category, subcategory)}>
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Subcategory">
                                    <IconButton size="small" color="error" onClick={() => deleteSubcategory(category.id, subcategory.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>

                              {/* Services */}
                              <Collapse in={expandedSubcategories.includes(subcategory.id)}>
                                <Box sx={{ mt: 2 }}>
                                  {subcategory.services.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 4 }}>
                                      No services yet. Add your first service!
                                    </Typography>
                                  ) : (
                                    <List dense disablePadding>
                                      {subcategory.services.map((service) => (
                                        <ListItem 
                                          key={service.id} 
                                          sx={{ 
                                            bgcolor: 'background.paper', 
                                            mb: 1, 
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                          }}
                                        >
                                          <HospitalIcon color="action" sx={{ mr: 2 }} />
                                          <ListItemText 
                                            primary={
                                              <Typography variant="subtitle2" fontWeight="bold">
                                                {service.name}
                                              </Typography>
                                            }
                                            secondary={service.description || 'No description'} 
                                          />
                                          <Chip 
                                            label={`₱${service.price.toFixed(2)}`} 
                                            color="warning" 
                                            size="small" 
                                            variant="outlined" 
                                            sx={{ mr: 2, fontWeight: 'bold' }} 
                                          />
                                          <ListItemSecondaryAction>
                                            <IconButton edge="end" size="small" onClick={() => openEditServiceModal(category, subcategory, service)} sx={{ mr: 1 }}>
                                              <EditIcon fontSize="small" color="info" />
                                            </IconButton>
                                            <IconButton edge="end" size="small" onClick={() => deleteService(category.id, subcategory.id, service.id)}>
                                              <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </Box>
                              </Collapse>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Add/Edit Category Modal */}
        <Dialog open={showCategoryModal} onClose={() => setShowCategoryModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon /> {editMode === 'category' ? 'Edit Category' : 'Add New Category'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              required
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g., Medical Consultations"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCategoryModal(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveCategory} disabled={!categoryName.trim()}>
              {editMode === 'category' ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Subcategory Modal */}
        <Dialog open={showSubcategoryModal} onClose={() => setShowSubcategoryModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon /> {editMode === 'subcategory' ? 'Edit Subcategory' : 'Add Subcategory'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Category: <strong>{selectedCategory?.name}</strong>
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Subcategory Name"
              fullWidth
              required
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              placeholder="e.g., General Consultation"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSubcategoryModal(false)}>Cancel</Button>
            <Button variant="contained" color="success" onClick={handleSaveSubcategory} disabled={!subcategoryName.trim()}>
              {editMode === 'subcategory' ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Service Modal */}
        <Dialog open={showServiceModal} onClose={() => setShowServiceModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <HospitalIcon /> {editMode === 'service' ? 'Edit Service' : 'Add Service'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Alert severity="warning" icon={<HospitalIcon />} sx={{ mb: 2 }}>
              <strong>{selectedCategory?.name}</strong> → <strong>{selectedSubcategory?.name}</strong>
            </Alert>
            <Stack spacing={2}>
              <TextField
                autoFocus
                label="Service Name"
                fullWidth
                required
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
              />
              <TextField
                label="Price"
                type="number"
                fullWidth
                required
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowServiceModal(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="warning" 
              onClick={handleSaveService} 
              disabled={!serviceName.trim() || !servicePrice}
            >
              {editMode === 'service' ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Services;
